<template>
  <div class="skeleton-game-menu">
    <!-- First Row - Game Categories Skeleton -->
    <section class="skeleton-game-categories">
      <div class="skeleton-categories-grid">
        <div
          v-for="index in 3"
          :key="`category-${index}`"
          class="skeleton-category-card"
        >
          <div class="skeleton-category-background">
            <div class="skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Second Row - Game Types Skeleton -->
    <section class="skeleton-game-types">
      <div class="skeleton-game-types-container">
        <div
          v-for="index in gameTypeCount"
          :key="`type-${index}`"
          class="skeleton-game-type-item"
        >
          <div class="skeleton-game-type-icon">
            <div class="skeleton-shimmer"></div>
          </div>
          <div class="skeleton-game-type-label">
            <div class="skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SkeletonGameMenu',
  data() {
    return {
      gameTypeCount: 6 // Show 6 game type skeletons initially
    }
  },
  mounted() {
    this.adjustGameTypeCount()
    window.addEventListener('resize', this.adjustGameTypeCount)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustGameTypeCount)
  },
  methods: {
    adjustGameTypeCount() {
      const viewportWidth = window.innerWidth
      if (viewportWidth <= 480) {
        this.gameTypeCount = 4
      } else if (viewportWidth <= 768) {
        this.gameTypeCount = 5
      } else {
        this.gameTypeCount = 6
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.skeleton-game-menu {
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
}

// Game Categories Section
.skeleton-game-categories {
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
}

.skeleton-categories-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 0 20px;

  @media (max-width: 768px) {
    gap: 8px;
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    padding: 0 12px;
  }
}

.skeleton-category-card {
  position: relative;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    rgba(26, 26, 46, 0.6),
    rgba(22, 33, 62, 0.6)
  );

  @media (max-width: 768px) {
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
  }
}

.skeleton-category-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(45, 45, 65, 0.4),
    rgba(35, 45, 75, 0.4)
  );
  border-radius: inherit;
  overflow: hidden;
}

// Game Types Section
.skeleton-game-types {
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
}

.skeleton-game-types-container {
  display: flex;
  gap: 12px;
  overflow-x: hidden;
  padding-bottom: 8px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
}

.skeleton-game-type-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  min-width: 80px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(26, 26, 46, 0.4),
    rgba(22, 33, 62, 0.4)
  );
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 10px 14px;
    min-width: 70px;
    border-radius: 10px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    min-width: 60px;
    border-radius: 8px;
    gap: 5px;
  }
}

.skeleton-game-type-icon {
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
}

.skeleton-game-type-label {
  position: relative;
  width: 50px;
  height: 14px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 45px;
    height: 12px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 10px;
  }
}

// Shimmer animation
.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
  animation: shimmer 2.8s infinite;
  border-radius: inherit;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Performance optimizations
.skeleton-game-menu {
  will-change: contents;
  transform: translate3d(0, 0, 0);
}

.skeleton-game-types-container {
  contain: layout style paint;
}

// Additional styling to match GameMenu hover effects
.skeleton-category-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.4) 100%
  );
  z-index: 1;
  border-radius: inherit;
  pointer-events: none;
}
</style>
